/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSearchParams } from 'react-router-dom';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { pipelinesService } from '../../services/pipelines.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { PipelineRun } from '../../types';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { formatDate } from 'sharedUi/dateUtils';
import { runTone } from '../pipelines/pipeline-status';
import { StatusChip } from '../../components/common/panel';
import { PANEL } from '../../components/common/ui-kit';

const PipelineRunList = ({ embedded = false, pipelineId }: { embedded?: boolean; pipelineId?: string }) => {
  const { navigateToView } = useSecureNavigation({ entity: 'pipeline-run', basePath: '/pipeline-runs' });
  const toast = useRef<Toast>(null);
  const [searchParams] = useSearchParams();
  const pipelineFilter = pipelineId || searchParams.get('pipelineId') || undefined;

  const [runs, setRuns] = useState<PipelineRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 10000 }), []);

  const fetchRuns = useCallback(async (page = 1, size = pageSize) => {
    try {
      setLoading(true);
      const res = await pipelinesService.listRuns({ page, page_size: size, pipeline_id: pipelineFilter });
      setRuns(res.data || []);
      setTotal(res.pagination?.totalItems ?? res.pagination?.total ?? res.total ?? 0);
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to load pipeline runs'));
    } finally {
      setLoading(false);
    }
  }, [pageSize, pipelineFilter, showError]);

  useEffect(() => { setFirst(0); fetchRuns(1, pageSize); }, [pipelineFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the list live while any run on the page is still going.
  useEffect(() => {
    if (!runs.some((r) => r.status === 'QUEUED' || r.status === 'RUNNING')) return;
    const t = setInterval(() => fetchRuns(Math.floor(first / pageSize) + 1, pageSize), 3000);
    return () => clearInterval(t);
  }, [runs, first, pageSize, fetchRuns]);

  const columns: SaasColumn<PipelineRun>[] = useMemo(() => [
    { field: 'pipelineRunId', header: 'Run ID', hideable: false, body: (r) => <span className="font-mono text-xs text-gray-700 dark:text-gray-200">{r.pipelineRunId}</span> },
    ...(pipelineFilter ? [] : [{ field: 'pipelineCode', header: 'Pipeline', body: (r: PipelineRun) => <span className="font-mono text-xs text-gray-700 dark:text-gray-200">{r.pipelineCode || r.pipelineRef}</span> }]),
    { field: 'status', header: 'Status', body: (r) => (
      <div className="flex items-center gap-2">
        <StatusChip tone={runTone(r.status)}>{r.status}</StatusChip>
        {(r.status === 'RUNNING' || r.status === 'QUEUED') && r.progress?.sourcesTotal ? (
          <span className="text-xs tabular-nums text-ink-muted">{r.progress.sourcesDone ?? 0}/{r.progress.sourcesTotal}</span>) : null}
      </div>
    ) },
    { field: 'sources', header: 'Sources', align: 'right', body: (r) => (
      <span className="tabular-nums">
        {r.statistics?.sourcesProcessed ?? 0}/{r.statistics?.sourcesRequested ?? 0}
        {r.statistics?.sourcesFailed ? <span className="ui-ink ui-ink--bad"> ({r.statistics.sourcesFailed} failed)</span> : null}
      </span>) },
    { field: 'chunks', header: 'Chunks', align: 'right', body: (r) => (r.statistics?.chunksCreated != null
      ? <span className="tabular-nums">{r.statistics.chunksCreated.toLocaleString()}</span>
      : <span className="text-ink-muted">—</span>) },
    { field: 'duration', header: 'Duration', align: 'right', body: (r) => (r.statistics?.durationSeconds
      ? <span className="tabular-nums">{r.statistics.durationSeconds.toFixed(1)}s</span>
      : <span className="text-ink-muted">—</span>) },
    { field: 'createdAt', header: 'Started', body: (r) => <span className="tabular-nums">{formatDate(r.startedAt || r.createdAt)}</span> },
    { field: 'actions', header: '', align: 'center', hideable: false, body: (r) => (
      <button type="button" className="ui-press flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => navigateToView(r._id)} data-pr-tooltip="View" aria-label="View run">
        <i className="pi pi-eye ui-act--view text-[1.05rem]" aria-hidden />
      </button>
    ) },
  ], [pipelineFilter, navigateToView]);

  const table = (
    <SaasDataTable tableId="pipeline-run" columns={columns} data={runs} loading={loading} dataKey="_id"
      emptyMessage="No pipeline runs yet" first={first} rows={pageSize} totalRecords={total}
      onPage={(e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRuns(Math.floor(e.first / e.rows) + 1, e.rows); }}
      rowsPerPageOptions={[5, 10, 25, 50]} onSort={() => {}} onFilter={() => {}} testId="PipelineRunList"
      toolbarEnd={<><span className="whitespace-nowrap text-sm tabular-nums text-ink-muted">{total} {total === 1 ? 'run' : 'runs'}</span>
        <RefreshButton onClick={() => fetchRuns(Math.floor(first / pageSize) + 1, pageSize)} loading={loading} /></>} />
  );

  if (embedded) return <div data-testid="SCR-PipelineRun-List"><Toast ref={toast} position="top-right" /><div className={`${PANEL} overflow-hidden`}>{table}</div></div>;
  return (
    <PermissionGuard jobName={JOB_NAMES.PIPELINE_RUNS}>
      <Toast ref={toast} position="top-right" />
      <div className="p-6" data-testid="SCR-PipelineRun-List">
        <PageHeader icon="pi pi-play" title="Pipeline Runs" subtitle="Every execution, what it processed, and what it produced" testId="PipelineRun.Header" />
        <div className={`${PANEL} overflow-hidden`}>{table}</div>
      </div>
    </PermissionGuard>
  );
};

export default PipelineRunList;
